import {
  ChangeDetectorRef, Component, OnDestroy, OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { UsersService } from 'src/app/services/users.service';

@Component({
  selector: 'app-user-search',
  templateUrl: './user-search.component.html',
  styleUrls: [ './user-search.component.scss' ],
})
export class UserSearchComponent implements OnInit, OnDestroy {
  isVisible = false;
  searchInputEnabled = true;
  users: any[] = [];
  private permissions: any;
  private debounceTimer: any;
  private permissionsSubscription: Subscription;
  private routerSubscription: Subscription;
  private searchSubscription: Subscription;

  constructor(
    private router: Router,
    private authService: AuthService,
    private usersService: UsersService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    this.permissionsSubscription = this.authService.permissions$
      .subscribe((permissions: any) => {
        this.permissions = permissions;
        this.updateVisibility();
      });

    this.routerSubscription = this.router.events.subscribe(() => {
      this.updateVisibility();
      this.clearSearch();
    });

    this.searchSubscription = this.usersService.userSearchResults$
      .subscribe((data: any) => {
        this.users = data?.rows || [];
      });
  }

  private updateVisibility() {
    const urlSegments = this.router.url.split('/');
    const isProfileRoute = urlSegments[1] === 'profile' && urlSegments.length > 2 && /^\d+$/.test(urlSegments[2]);
    this.isVisible = isProfileRoute && this.authService.hasManageStaffPermission(this.permissions);
  }

  onInput(event: any) {
    const searchText = event.target.value;
    clearTimeout(this.debounceTimer);
    if (!searchText?.trim()) {
      this.users = [];
      this.usersService.clearSearchResults();
      return;
    }
    const isDatalistSelection = !event.inputType || event.inputType === 'insertReplacementText';
    if (isDatalistSelection) {
      const selectedUser = this.users.find((user: any) => user.fullName === searchText);
      if (selectedUser) {
        this.clearSearch();
        this.router.navigate([ `/profile/${selectedUser.id}` ]);
        return;
      }
    }
    this.debounceTimer = setTimeout(() => {
      this.usersService.searchUsers(searchText);
    }, 300);
  }

  onUserSelected(event: any) {
    clearTimeout(this.debounceTimer);
    const selectedName = event.target.value;
    const selectedUser = this.users.find((user: any) => user.fullName === selectedName);
    if (selectedUser) {
      this.clearSearch();
      this.router.navigate([ `/profile/${selectedUser.id}` ]);
    }
  }

  onEnter(event: any) {
    const searchText = event.target.value?.trim();
    if (!searchText) return;
    // Match the browser's native datalist filtering: this.users holds the full
    // API result set, but the visible dropdown is narrowed to the typed text
    const matches = this.users.filter((user: any) => user.fullName?.toLowerCase().includes(searchText.toLowerCase()));
    const exact = matches.find((user: any) => user.fullName?.toLowerCase() === searchText.toLowerCase());
    const target = exact || (matches.length === 1 ? matches[0] : null);
    if (target) {
      event.preventDefault();
      this.clearSearch();
      this.router.navigate([ `/profile/${target.id}` ]);
    }
  }

  // Recreates the input element to reset Firefox's corrupted placeholder state
  // (:placeholder-shown stays true with a non-empty value after datalist interactions)
  private clearSearch() {
    clearTimeout(this.debounceTimer);
    this.searchInputEnabled = false;
    this.cdr.detectChanges();
    this.searchInputEnabled = true;
    this.cdr.detectChanges();
    this.users = [];
    this.usersService.clearSearchResults();
  }

  ngOnDestroy() {
    clearTimeout(this.debounceTimer);
    this.permissionsSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
    this.searchSubscription?.unsubscribe();
  }
}
